import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  FileText,
  User,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Edit,
  Send,
  ArrowRight,
  File,
  Image as ImageIcon,
  Printer,
  RotateCcw,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { formatDate, formatTimeAgo, getStatusText, getStatusColor, formatArea } from '../../utils/helpers';
import PermitHistoryDetail from '../../components/PermitHistoryDetail';
import FileUploadHistory from '../../components/FileUploadHistory';
import PermitMap from '../../components/PermitMap';
import OTPVerificationCompact from '../../components/OTPVerificationCompact';
import OTPVerificationCancellation from '../../components/OTPVerificationCancellation';
import EditRequestBanner from '../../components/EditRequestBanner';
import EditRequestsList from '../../components/EditRequestsList';
import CancellationRequestsList from '../../components/CancellationRequestsList';

const PermitView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewComments, setReviewComments] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [forwardToLevel, setForwardToLevel] = useState('');
  const [forwardToUser, setForwardToUser] = useState('');
  const [returnToLevel, setReturnToLevel] = useState('');
  const [returnToUser, setReturnToUser] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showCancelOTPStep, setShowCancelOTPStep] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelRemarks, setCancelRemarks] = useState('');
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [previewUrls, setPreviewUrls] = useState({
    landRegistry: null,
    registryMap: null,
    supporting: []
  });

  const { data: permitData, isLoading, error } = useQuery(
    ['permit', id],
    async () => {
      const response = await api.get(`/permits/${id}`);
      return response.data.permit;
    },
    {
      retry: 1
    }
  );

  // Fetch pending edit request for this permit (only for Active permits)
  const { data: pendingEditData } = useQuery(
    ['pendingEdit', id],
    async () => {
      const response = await api.get(`/edit-requests/${id}/pending`);
      return response.data.editRequest;
    },
    {
      enabled: permitData?.status === 'Active',
      retry: false,
      onError: () => {
        // No pending edit request exists - this is expected
      }
    }
  );

  // Fetch pending cancellation request for this permit
  const { data: pendingCancellationData } = useQuery(
    ['pendingCancellation', id],
    async () => {
      const response = await api.get(`/cancellation-requests/${id}/pending`);
      return response.data;
    },
    {
      enabled: !!permitData && ['Active', 'Renewal_Needed'].includes(permitData?.status),
      retry: false,
      onError: () => {
        // No pending cancellation request exists - this is expected
      }
    }
  );

  // Fetch reviewers list for selection (EAC, SDO, ADC, DC)
  // Only needed for BO (Submit for Review) and reviewers (Forward to next level during approval)
  const { data: reviewersData } = useQuery(
    'reviewers',
    async () => {
      const response = await api.get(`/users/reviewers/list?subdivisionId=${permitData?.subdivisionId}`);
      return response.data.reviewers;
    },
    {
      enabled: (user?.role === 'bo' && showSubmitDialog) || (['eac', 'sdo', 'adc', 'dc'].includes(user?.role) && showReviewDialog),
      retry: 1
    }
  );

  // Fetch return reason when permit is Returned
  const { data: returnReasonData } = useQuery(
    ['returnReason', id],
    async () => {
      const response = await api.get(`/workflow/return-reason/${id}`);
      return response.data.returnReason;
    },
    {
      enabled: permitData?.status === 'Returned',
      retry: false
    }
  );

  // Submit permit mutation
  const submitMutation = useMutation(
    async ({ assignedLevel, assignedUser }) => {
      const response = await api.post(`/permits/${id}/submit`, { assignedLevel, assignedUser });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['permit', id]);
        queryClient.invalidateQueries('permits');
        toast.success('Permit submitted for review successfully!');
        setShowSubmitDialog(false);
        setSelectedLevel('');
        setSelectedUser('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to submit permit');
      }
    }
  );

  // Review mutation for all reviewer roles (EAC, SDO, ADC, DC)
  const reviewMutation = useMutation(
    async ({ action, comments, forwardToLevel, forwardToUser, returnToLevel, returnToUser }) => {
      const payload = { action, comments };
      if (forwardToLevel) payload.forwardToLevel = forwardToLevel;
      if (forwardToUser) payload.forwardToUser = forwardToUser;
      if (returnToLevel) payload.returnToLevel = returnToLevel;
      if (returnToUser) payload.returnToUser = returnToUser;

      const response = await api.post(`/workflow/${id}/review`, payload);
      return { ...response.data, action };
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('permits');
        queryClient.invalidateQueries('workflow');
        toast.success('Review submitted successfully!');
        setShowReviewDialog(false);
        setReviewComments('');
        setForwardToLevel('');
        setForwardToUser('');
        setReturnToLevel('');
        setReturnToUser('');

        // Redirect to workflow after return/reject since the reviewer
        // may no longer have access to view the permit
        if (data.action === 'return' || data.action === 'reject') {
          navigate('/workflow');
        } else {
          queryClient.invalidateQueries(['permit', id]);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to submit review');
      }
    }
  );

  // Cancel permit mutation (DC only)
  const cancelMutation = useMutation(
    async ({ reason, remarks }) => {
      const response = await api.post(`/workflow/${id}/cancel`, { reason, remarks });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['permit', id]);
        queryClient.invalidateQueries('permits');
        toast.success('Permit cancelled successfully!');
        setShowCancelDialog(false);
        setShowCancelOTPStep(false);
        setCancelReason('');
        setCancelRemarks('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to cancel permit');
      }
    }
  );

  // Get file preview - shows actual content for images, icon for PDFs
  const getFilePreview = (document, size = 'large', previewUrl = null) => {
    const iconSize = size === 'large' ? 'h-32 w-32' : 'h-24 w-24';

    if (document.mimetype?.includes('image') && previewUrl) {
      // Show actual image preview using pre-signed URL
      return (
        <div className={`${iconSize} rounded-lg overflow-hidden border-2 border-gray-200 bg-white`}>
          <img
            src={previewUrl}
            alt={document.originalName}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to icon if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div style={{ display: 'none' }} className="w-full h-full flex items-center justify-center">
            <ImageIcon className={`${iconSize} text-blue-500`} />
          </div>
        </div>
      );
    } else if (document.mimetype?.includes('pdf')) {
      // Show PDF icon with preview indicator
      return (
        <div className="relative">
          <File className={`${iconSize} text-red-500`} />
          <div className="absolute bottom-0 right-0 bg-red-100 text-red-700 text-xs px-2 py-1 rounded">
            PDF
          </div>
        </div>
      );
    }
    // Default file icon
    return <FileText className={`${iconSize} text-gray-500`} />;
  };

  // Handle document download from S3
  const handleDownload = async (documentType, index = null) => {
    try {
      const docId = documentType === 'landRegistry' ? 'landRegistry' :
                    documentType === 'registryMap' ? 'registryMap' :
                    `supporting-${index}`;
      setDownloadingDoc(docId);

      // Build query parameters
      const params = new URLSearchParams({ documentType });
      if (index !== null) {
        params.append('index', index);
      }

      // Call API to get pre-signed URL
      const response = await api.get(`/permits/${id}/documents/download?${params}`);

      if (response.data.success) {
        // Open the pre-signed URL in a new tab to download
        window.open(response.data.downloadUrl, '_blank');
        toast.success(`Downloading ${response.data.fileName}`);
      } else {
        toast.error('Failed to generate download link');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.message || 'Failed to download document');
    } finally {
      setDownloadingDoc(null);
    }
  };

  // Handle download all documents as ZIP
  const handleDownloadAllZip = async () => {
    try {
      setDownloadingZip(true);

      // Call backend endpoint with responseType blob
      const response = await api.get(`/permits/${id}/documents/download-zip`, {
        responseType: 'blob'
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Set filename based on permit number
      const fileName = `${permit.permit_no.replace(/\//g, '-')}.zip`;
      link.setAttribute('download', fileName);

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('All documents downloaded successfully!');
    } catch (error) {
      console.error('ZIP download error:', error);
      toast.error(error.response?.data?.message || 'Failed to download documents as ZIP');
    } finally {
      setDownloadingZip(false);
    }
  };

  // Handle print permit (PDF with permit information only)
  const printPermit = async () => {
    try {
      toast.loading('Generating print document...', { id: 'pdf-print' });

      const response = await api.get(
        `/permits/${id}/print`,
        { responseType: 'blob' }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `permit-${id}-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Print document generated successfully!', { id: 'pdf-print' });
    } catch (error) {
      console.error('Print PDF error:', error);
      toast.error('Failed to generate print document', { id: 'pdf-print' });
    }
  };

  // Fetch pre-signed URLs for image previews when permit data loads
  useEffect(() => {
    const fetchPreviewUrls = async () => {
      if (!permitData) return;

      try {
        const urls = {
          landRegistry: null,
          registryMap: null,
          supporting: []
        };

        // Fetch land registry document preview URL if it's an image and active
        if (permitData.landRegistryDocument?.mimetype?.includes('image') &&
            permitData.landRegistryDocument?.isActive !== false) {
          const response = await api.get(`/permits/${id}/documents/download?documentType=landRegistry`);
          if (response.data.success) {
            urls.landRegistry = response.data.downloadUrl;
          }
        }

        // Fetch registry map document preview URL if it's an image and active
        if (permitData.registryMapDocument?.mimetype?.includes('image') &&
            permitData.registryMapDocument?.isActive !== false) {
          const response = await api.get(`/permits/${id}/documents/download?documentType=registryMap`);
          if (response.data.success) {
            urls.registryMap = response.data.downloadUrl;
          }
        }

        // Fetch supporting documents preview URLs for images
        if (permitData.scannedDocuments && permitData.scannedDocuments.length > 0) {
          const promises = permitData.scannedDocuments.map(async (doc, index) => {
            if (doc.mimetype?.includes('image')) {
              try {
                const response = await api.get(`/permits/${id}/documents/download?documentType=supporting&index=${index}`);
                if (response.data.success) {
                  return response.data.downloadUrl;
                }
              } catch (error) {
                console.error(`Error fetching preview for document ${index}:`, error);
              }
            }
            return null;
          });

          urls.supporting = await Promise.all(promises);
        }

        setPreviewUrls(urls);
      } catch (error) {
        console.error('Error fetching preview URLs:', error);
      }
    };

    fetchPreviewUrls();
  }, [permitData, id]);

  const handleSubmit = () => {
    if (!selectedLevel || !selectedUser) {
      toast.error('Please select a level and user to assign this permit');
      return;
    }
    submitMutation.mutate({ assignedLevel: selectedLevel, assignedUser: selectedUser });
  };

  const handleReviewAction = (action) => {
    setReviewAction(action);
    setShowReviewDialog(true);
  };

  const handleReviewSubmit = () => {
    if (!reviewComments.trim()) {
      toast.error('Please enter comments');
      return;
    }

    // Validate selections based on action
    if (reviewAction === 'approve' && user.role !== 'dc') {
      if (!forwardToLevel || !forwardToUser) {
        toast.error('Please select a level and user to forward the permit to');
        return;
      }
    }

    if (reviewAction === 'return') {
      if (!returnToLevel || !returnToUser) {
        toast.error('Please select a level and user to return the permit to');
        return;
      }
    }

    // Submit review with appropriate parameters
    reviewMutation.mutate({
      action: reviewAction,
      comments: reviewComments,
      forwardToLevel: reviewAction === 'approve' ? forwardToLevel : undefined,
      forwardToUser: reviewAction === 'approve' ? forwardToUser : undefined,
      returnToLevel: reviewAction === 'return' ? returnToLevel : undefined,
      returnToUser: reviewAction === 'return' ? returnToUser : undefined
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading permit</h3>
              <p className="mt-2 text-sm text-red-700">
                {error.response?.data?.message || 'Failed to load permit details'}
              </p>
              <button 
                onClick={() => navigate('/permits')}
                className="mt-3 text-sm text-red-600 hover:text-red-500"
              >
                ← Back to permits list
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!permitData) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className="mt-2 text-lg font-medium text-secondary-900">Permit not found</h3>
          <p className="mt-1 text-secondary-600">The requested permit could not be found.</p>
          <button 
            onClick={() => navigate('/permits')}
            className="mt-4 btn btn-outline"
          >
            ← Back to permits list
          </button>
        </div>
      </div>
    );
  }

  const permit = permitData;

  // Check if ADC is the head of the permit's subdivision (for final approval capability)
  // Only subdivision heads can give final approval; other ADCs behave like SDO/EAC
  const isAdcSubdivisionHead = user.role === 'adc' && user.isSubdivisionHead;

  // Determine if user can edit this permit
  const canEdit =
    (permit.submittedBy === user.id && permit.status === 'Draft') || // All users: own drafts only
    (user.role === 'eac' && permit.status === 'Pending_EAC_Review' && permit.assignedEAC === user.id) || // EAC: only at their review stage
    (user.role === 'sdo' && permit.status === 'Pending_SDO_Review' && permit.assignedSDO === user.id) || // SDO: only at their review stage
    (user.role === 'adc' && permit.status === 'Pending_ADC_Review' && permit.assignedADC === user.id) || // ADC: at their review stage
    (user.role === 'adc' && permit.status === 'Active' && isAdcSubdivisionHead); // ADC: Active permits (only if subdivision head)

  // Only BO can submit draft/returned permits (reviewers are auto-assigned on creation)
  // For returned permits, BO must edit first (resolve the return) before submitting
  const canSubmit = user.role === 'bo' && permit.submittedBy === user.id && (
    permit.status === 'Draft' ||
    (permit.status === 'Returned' && returnReasonData?.resolved)
  );

  // Reviewers can review permits assigned to them at their level
  const canReview =
    (user.role === 'eac' && permit.status === 'Pending_EAC_Review' && permit.assignedEAC === user.id) ||
    (user.role === 'sdo' && permit.status === 'Pending_SDO_Review' && permit.assignedSDO === user.id) ||
    (user.role === 'adc' && permit.status === 'Pending_ADC_Review' && permit.assignedADC === user.id) ||
    (user.role === 'dc' && permit.status === 'Pending_DC_Approval');

  // ADC can give final approval only if they are the subdivision head
  const canAdcFinalApprove = user.role === 'adc' && permit.status === 'Pending_ADC_Review' &&
    permit.assignedADC === user.id && isAdcSubdivisionHead;

  // DC or ADC (subdivision head only) can cancel Active permits
  const canCancel = permit.status === 'Active' && (
    user.role === 'dc' ||
    (user.role === 'adc' && isAdcSubdivisionHead)
  );

  // DC and ADC subdivision heads already have direct Edit and Cancel Permit buttons,
  // so they should not see the workflow-based Request Edit / Request Cancellation buttons.
  const canRequestEdit = ['Active', 'Renewal_Needed'].includes(permit.status) && !pendingEditData &&
    ['bo', 'eac', 'sdo', 'adc'].includes(user.role) &&
    !isAdcSubdivisionHead;

  // DC already has Cancel Permit button; ADC subdivision heads too — exclude both.
  const canRequestCancellation = ['Active', 'Renewal_Needed'].includes(permit.status) &&
    !pendingCancellationData?.hasPending &&
    ['bo', 'eac', 'sdo', 'adc'].includes(user.role) &&
    !isAdcSubdivisionHead;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Review Dialog for Reviewers (EAC/SDO/ADC/DC) */}
      {showReviewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              {reviewAction === 'approve' && user.role === 'dc' && 'Final Approval - OTP Verification Required'}
              {reviewAction === 'final_approve' && user.role === 'adc' && 'ADC Final Approval - OTP Verification Required'}
              {reviewAction === 'approve' && user.role !== 'dc' && 'Approve Permit'}
              {reviewAction === 'reject' && 'Reject Permit'}
              {reviewAction === 'return' && 'Return for Corrections'}
            </h3>

            {/* Forward Selection (for approve action, except DC) */}
            {reviewAction === 'approve' && user.role !== 'dc' && (
              <>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Forward to Level <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={forwardToLevel}
                    onChange={(e) => {
                      setForwardToLevel(e.target.value);
                      setForwardToUser('');
                    }}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    disabled={reviewMutation.isLoading}
                  >
                    <option value="">Select a level...</option>
                    {user.role === 'eac' && (
                      <>
                        <option value="sdo">SDO (Sub-Divisional Officer)</option>
                        <option value="adc">ADC (Additional Deputy Commissioner)</option>
                        <option value="dc">DC (Deputy Commissioner)</option>
                      </>
                    )}
                    {user.role === 'sdo' && (
                      <>
                        <option value="adc">ADC (Additional Deputy Commissioner)</option>
                        <option value="dc">DC (Deputy Commissioner)</option>
                      </>
                    )}
                    {user.role === 'adc' && (
                      <option value="dc">DC (Deputy Commissioner)</option>
                    )}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Assign to User <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={forwardToUser}
                    onChange={(e) => setForwardToUser(e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    disabled={reviewMutation.isLoading || !forwardToLevel}
                  >
                    <option value="">
                      {forwardToLevel ? 'Select a user...' : 'Please select a level first'}
                    </option>
                    {forwardToLevel && reviewersData?.filter(r => r.role === forwardToLevel).map((reviewer) => (
                      <option key={reviewer.id} value={reviewer.id}>
                        {reviewer.fullName} - {reviewer.designation || reviewer.role.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Return Selection (for return action) */}
            {reviewAction === 'return' && (
              <>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Return to Level <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={returnToLevel}
                    onChange={(e) => {
                      setReturnToLevel(e.target.value);
                      setReturnToUser('');
                    }}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    disabled={reviewMutation.isLoading}
                  >
                    <option value="">Select a level...</option>
                    <option value="bo">BO (Branch Officer)</option>
                    {user.role === 'dc' && (
                      <>
                        <option value="eac">EAC (Extra Assistant Commissioner)</option>
                        <option value="sdo">SDO (Sub-Divisional Officer)</option>
                        <option value="adc">ADC (Additional Deputy Commissioner)</option>
                      </>
                    )}
                    {user.role === 'adc' && (
                      <>
                        <option value="eac">EAC (Extra Assistant Commissioner)</option>
                        <option value="sdo">SDO (Sub-Divisional Officer)</option>
                      </>
                    )}
                    {user.role === 'sdo' && (
                      <option value="eac">EAC (Extra Assistant Commissioner)</option>
                    )}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Assign to User <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={returnToUser}
                    onChange={(e) => setReturnToUser(e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    disabled={reviewMutation.isLoading || !returnToLevel}
                  >
                    <option value="">
                      {returnToLevel ? 'Select a user...' : 'Please select a level first'}
                    </option>
                    {returnToLevel === 'bo' ? (
                      // For BO, we need to get the original submitter
                      <option value={permitData?.submittedBy}>
                        {permitData?.submitter?.fullName} (Original Submitter)
                      </option>
                    ) : (
                      returnToLevel && reviewersData?.filter(r => r.role === returnToLevel).map((reviewer) => (
                        <option key={reviewer.id} value={reviewer.id}>
                          {reviewer.fullName} - {reviewer.designation || reviewer.role.toUpperCase()}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </>
            )}

            {/* DC Approval or ADC Final Approval with OTP Verification */}
            {((reviewAction === 'approve' && user.role === 'dc') || (reviewAction === 'final_approve' && user.role === 'adc')) ? (
              <>
                {/* Comments Section - At Top for Final Approval */}
                {/* <div className="mb-6">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    {user.role === 'adc' ? 'Final Approval Comments' : 'Approval Comments'} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={reviewComments}
                    onChange={(e) => setReviewComments(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder={user.role === 'adc' ? 'Enter your final approval comments for this subdivision permit...' : 'Enter your approval comments...'}
                    disabled={reviewMutation.isLoading}
                  />
                </div> */}

                {/* Security Verification Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-900">
                    <strong>Security Verification Required:</strong> Please verify your identity using the OTP sent to your registered mobile number.
                    {user.role === 'adc' && (
                      <span className="block mt-1 text-blue-700">
                        As ADC, you are giving final approval for this permit in your subdivision.
                      </span>
                    )}
                  </p>
                </div>

                {/* OTP Verification Compact Component - Combined with comments */}
                <OTPVerificationCompact
                  permitId={id}
                  reviewComments={reviewComments}
                  onSuccess={(approvalComments) => {
                    // Called after OTP is verified successfully
                    // Approve the permit with comments from OTP component
                    reviewMutation.mutate({
                      action: user.role === 'adc' ? 'final_approve' : 'approve',
                      comments: approvalComments || reviewComments
                    });
                  }}
                  onCancel={() => {
                    setShowReviewDialog(false);
                    setReviewComments('');
                    toast('Approval cancelled.');
                  }}
                  isLoading={reviewMutation.isLoading}
                />
              </>
            ) : (
              <>
                {/* Comments Section - For Non-DC Reviewers */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Comments <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={reviewComments}
                    onChange={(e) => setReviewComments(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter your comments..."
                  />
                </div>

                {/* Action Buttons - For Non-DC Reviewers */}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowReviewDialog(false);
                      setReviewComments('');
                      setForwardToLevel('');
                      setForwardToUser('');
                      setReturnToLevel('');
                      setReturnToUser('');
                    }}
                    className="btn btn-outline"
                    disabled={reviewMutation.isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReviewSubmit}
                    className={`btn ${
                      reviewAction === 'approve' ? 'btn-primary' :
                      reviewAction === 'reject' ? 'bg-red-600 hover:bg-red-700 text-white' :
                      'bg-yellow-600 hover:bg-yellow-700 text-white'
                    }`}
                    disabled={reviewMutation.isLoading}
                  >
                    {reviewMutation.isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        {reviewAction === 'approve' && (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve
                          </>
                        )}
                        {reviewAction === 'reject' && <><XCircle className="h-4 w-4 mr-2" /> Reject</>}
                        {reviewAction === 'return' && <><ArrowRight className="h-4 w-4 mr-2" /> Return</>}
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Cancel Permit Dialog with OTP Verification (DC/ADC) */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-secondary-900 mb-4 flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
              Cancel Active Permit
              {showCancelOTPStep && ' - OTP Verification'}
            </h3>

            {!showCancelOTPStep ? (
              /* Step 1: Collect Reason and Remarks */
              <>
                <p className="text-secondary-600 mb-4">
                  Are you sure you want to cancel this active permit? OTP verification will be required.
                </p>
                <div className="mb-4">
                  <label className="label">Cancellation Reason *</label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    rows={3}
                    className="input"
                    placeholder="Enter reason for cancellation..."
                  />
                </div>
                <div className="mb-4">
                  <label className="label">Additional Remarks (Optional)</label>
                  <textarea
                    value={cancelRemarks}
                    onChange={(e) => setCancelRemarks(e.target.value)}
                    rows={2}
                    className="input"
                    placeholder="Enter any additional remarks..."
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowCancelDialog(false);
                      setCancelReason('');
                      setCancelRemarks('');
                    }}
                    className="btn btn-outline"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      if (!cancelReason.trim()) {
                        toast.error('Please enter a cancellation reason');
                        return;
                      }
                      setShowCancelOTPStep(true);
                    }}
                    className="btn bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    Proceed to OTP Verification
                  </button>
                </div>
              </>
            ) : (
              /* Step 2: OTP Verification */
              <>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-orange-900">
                    <strong>Security Verification Required:</strong> Please verify your identity
                    using the OTP sent to your registered mobile number to confirm cancellation.
                  </p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-gray-700"><strong>Reason:</strong> {cancelReason}</p>
                  {cancelRemarks && (
                    <p className="text-sm text-gray-700 mt-1"><strong>Remarks:</strong> {cancelRemarks}</p>
                  )}
                </div>
                <OTPVerificationCancellation
                  permitId={id}
                  onSuccess={() => {
                    // OTP verified, now call cancel API
                    cancelMutation.mutate({ reason: cancelReason, remarks: cancelRemarks });
                  }}
                  onCancel={() => {
                    setShowCancelOTPStep(false);
                  }}
                  isLoading={cancelMutation.isLoading}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Submit Confirmation Dialog */}
      {showSubmitDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">Submit Permit for Review</h3>
            <p className="text-secondary-600 mb-4">
              Select a level and user to review this permit. You won't be able to edit it after submission.
            </p>

            {/* Level Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Submit to Level <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedLevel}
                onChange={(e) => {
                  setSelectedLevel(e.target.value);
                  setSelectedUser(''); // Reset user selection when level changes
                }}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={submitMutation.isLoading}
              >
                <option value="">Select a level...</option>
                <option value="eac">EAC (Extra Assistant Commissioner)</option>
                <option value="sdo">SDO (Sub-Divisional Officer)</option>
                <option value="adc">ADC (Additional Deputy Commissioner)</option>
                <option value="dc">DC (Deputy Commissioner)</option>
              </select>
            </div>

            {/* User Selection - Filtered by Selected Level */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Assign to User <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={submitMutation.isLoading || !selectedLevel}
              >
                <option value="">
                  {selectedLevel ? 'Select a user...' : 'Please select a level first'}
                </option>
                {selectedLevel && reviewersData?.filter(r => r.role === selectedLevel).map((reviewer) => (
                  <option key={reviewer.id} value={reviewer.id}>
                    {reviewer.fullName} - {reviewer.designation || reviewer.role.toUpperCase()}
                  </option>
                ))}
              </select>
              {selectedLevel && reviewersData?.filter(r => r.role === selectedLevel).length === 0 && (
                <p className="mt-1 text-sm text-red-500">
                  No users found for the selected level
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSubmitDialog(false);
                  setSelectedLevel('');
                  setSelectedUser('');
                }}
                className="btn btn-outline"
                disabled={submitMutation.isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="btn btn-primary"
                disabled={submitMutation.isLoading || !selectedLevel || !selectedUser}
              >
                {submitMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/permits')}
          className="flex items-center text-secondary-600 hover:text-secondary-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Permits
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">{permit.permit_no}</h1>
            <div className="mt-2">
              <span className={`status-badge ${getStatusColor(permit.status)}`}>
                {getStatusText(permit.status)}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Button Order: 1. Approve (or Final Approve + Escalate for ADC), 2. Return, 3. Edit, 4. Reject, 5. Print */}

            {/* 1. Approve Buttons - ADC with final approval capability gets two buttons */}
            {canReview && canAdcFinalApprove && (
              <>
                {/* Final Approve - for ADC in permit's subdivision */}
                <button
                  onClick={() => handleReviewAction('final_approve')}
                  className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={reviewMutation.isLoading}
                  title="Give final approval for this permit in your subdivision"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Final Approve
                </button>
                {/* Escalate to DC */}
                <button
                  onClick={() => handleReviewAction('approve')}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={reviewMutation.isLoading}
                  title="Forward this permit to DC for final approval"
                >
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Escalate to DC
                </button>
              </>
            )}

            {/* Regular Approve Button - for reviewers without final approval capability */}
            {canReview && !canAdcFinalApprove && (
              <button
                onClick={() => handleReviewAction('approve')}
                className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={reviewMutation.isLoading}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </button>
            )}


            {/* 2. Edit Button */}
            {canEdit && (
              <Link
                to={`/permits/${permit.id}/edit`}
                className="inline-flex items-center px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg border border-gray-300 shadow-sm transition-colors duration-200"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Link>
            )}

            {/* 3. Return Button */}
            {canReview && (
              <button
                onClick={() => handleReviewAction('return')}
                className="inline-flex items-center px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg border border-gray-300 shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={reviewMutation.isLoading}
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Return
              </button>
            )}

            

            {/* 4. Reject Button */}
            {canReview && (
              <button
                onClick={() => handleReviewAction('reject')}
                className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={reviewMutation.isLoading}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </button>
            )}

            {/* 5. Print Button - Available to all workflow roles */}
            {['bo', 'eac', 'sdo', 'adc', 'dc'].includes(user?.role) && (
              <button
                onClick={printPermit}
                className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg shadow-sm transition-colors duration-200"
                title="Print permit information only"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </button>
            )}

            {/* DC/ADC Cancel Active Permit Button */}
            {canCancel && (
              <button
                onClick={() => setShowCancelDialog(true)}
                className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={cancelMutation.isLoading}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancel Permit
              </button>
            )}

            {/* Submit for Review Button */}
            {canSubmit && (
              <button
                onClick={() => setShowSubmitDialog(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-colors duration-200"
              >
                <Send className="h-4 w-4 mr-2" />
                Submit for Review
              </button>
            )}

            {/* Request Edit Button - For Active permits without pending edits */}
            {canRequestEdit && (
              <Link
                to={`/permits/${permit.id}/request-edit`}
                className="inline-flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg shadow-sm transition-colors duration-200"
              >
                <Edit className="h-4 w-4 mr-2" />
                Request Edit
              </Link>
            )}

            {/* Request Cancellation Button - For Active/Renewal_Needed permits */}
            {canRequestCancellation && (
              <Link
                to={`/permits/${permit.id}/cancellation-request/create`}
                className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-colors duration-200"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Request Cancellation
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Pending Cancellation Request Alert */}
      {pendingCancellationData?.hasPending && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">
                Cancellation Request Pending
              </h3>
              <p className="mt-1 text-sm text-red-700">
                A cancellation request for this permit is currently under review.
              </p>
              <p className="mt-1 text-xs text-red-600">
                Status: {pendingCancellationData.cancellationRequest.status.replace(/_/g, ' ')}
              </p>
              <Link
                to={`/cancellation-requests/${pendingCancellationData.cancellationRequest.id}`}
                className="mt-2 inline-flex items-center text-sm font-medium text-red-600 hover:text-red-500"
              >
                View Cancellation Request →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Pending Edit Request Banner */}
      {pendingEditData && (
        <EditRequestBanner editRequest={pendingEditData} />
      )}

      {/* Return Reason Banner - hidden once permit is edited after return */}
      {permit.status === 'Returned' && returnReasonData && !returnReasonData.resolved && (
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
          <div className="flex items-start">
            <RotateCcw className="h-5 w-5 text-orange-500 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-orange-800">
                Permit Returned by {returnReasonData.returnedByRole?.toUpperCase()} - {returnReasonData.returnedBy}
              </h3>
              <div className="mt-2 p-3 bg-white rounded border border-orange-200">
                <div className="flex items-start">
                  <MessageSquare className="h-4 w-4 text-orange-400 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-800">{returnReasonData.comments}</p>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <p className="text-xs text-orange-600">
                  Returned on {formatDate(returnReasonData.returnedAt)}
                </p>
                {permit.submittedBy === user.id && (
                  <Link
                    to={`/permits/${id}/edit`}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-100 rounded-lg hover:bg-orange-200 transition-colors"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit & Resubmit
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Basic Information */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-secondary-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-secondary-700">Permit Holder Name</label>
            <p className="mt-1 text-secondary-900">{permit.name}</p>
          </div>
          {permit.father_name && (
            <div>
              <label className="text-sm font-medium text-secondary-700">Father's Name</label>
              <p className="mt-1 text-secondary-900">{permit.father_name}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-secondary-700">Village/Ward</label>
            <p className="mt-1 text-secondary-900 flex items-center">
              <MapPin className="h-4 w-4 mr-2 text-secondary-500" />
              {permit.village}
            </p>
          </div>
          {permit.subdivision && (
            <div>
              <label className="text-sm font-medium text-secondary-700">Subdivision</label>
              <p className="mt-1 text-secondary-900">{permit.subdivision.name}</p>
            </div>
          )}
          {permit.post_office && (
            <div>
              <label className="text-sm font-medium text-secondary-700">Post Office</label>
              <p className="mt-1 text-secondary-900">{permit.post_office}</p>
            </div>
          )}
          {permit.police_station && (
            <div>
              <label className="text-sm font-medium text-secondary-700">Police Station</label>
              <p className="mt-1 text-secondary-900">{permit.police_station}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-secondary-700">District</label>
            <p className="mt-1 text-secondary-900">{permit.district}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-secondary-700">Size</label>
            <p className="mt-1 text-secondary-900">{permit.size_sqft}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-secondary-700">Issue Date</label>
            <p className="mt-1 text-secondary-900 flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-secondary-500" />
              {formatDate(permit.issue_date)}
            </p>
          </div>
          {permit.validity_date && (
            <div>
              <label className="text-sm font-medium text-secondary-700">Validity Date</label>
              <p className="mt-1 text-secondary-900 flex items-center">
                <Calendar className="h-4 w-4 mr-2 text-secondary-500" />
                {formatDate(permit.validity_date)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Location Map - Show as separate section if coordinates exist */}
      {(permit.latitude && permit.longitude) && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">Permit Location</h3>
          <PermitMap
            latitude={permit.latitude}
            longitude={permit.longitude}
            boundaryPoints={permit.boundaryPoints}
            editable={false}
            height="450px"
            village={permit.village}
            permitNumber={permit.permit_no}
            showCenterOnly={!user}
          />
        </div>
      )}

      {/* Land Details */}
      {(permit.north_boundary || permit.south_boundary || permit.east_boundary || permit.west_boundary || permit.location_description || permit.remarks) && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">Land Details</h3>

          {/* Boundaries */}
          {(permit.north_boundary || permit.south_boundary || permit.east_boundary || permit.west_boundary) && (
            <div className="mb-6">
              <h4 className="text-md font-medium text-secondary-800 mb-3">Boundaries</h4>
              <div className="grid grid-cols-2 gap-4">
                {permit.north_boundary && (
                  <div>
                    <label className="text-sm font-medium text-secondary-700">North</label>
                    <p className="mt-1 text-secondary-900">{permit.north_boundary}</p>
                  </div>
                )}
                {permit.south_boundary && (
                  <div>
                    <label className="text-sm font-medium text-secondary-700">South</label>
                    <p className="mt-1 text-secondary-900">{permit.south_boundary}</p>
                  </div>
                )}
                {permit.east_boundary && (
                  <div>
                    <label className="text-sm font-medium text-secondary-700">East</label>
                    <p className="mt-1 text-secondary-900">{permit.east_boundary}</p>
                  </div>
                )}
                {permit.west_boundary && (
                  <div>
                    <label className="text-sm font-medium text-secondary-700">West</label>
                    <p className="mt-1 text-secondary-900">{permit.west_boundary}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {permit.location_description && (
            <div className="mb-4">
              <label className="text-sm font-medium text-secondary-700">Location Description</label>
              <p className="mt-1 text-secondary-900 whitespace-pre-wrap">{permit.location_description}</p>
            </div>
          )}

          {permit.remarks && (
            <div>
              <label className="text-sm font-medium text-secondary-700">Remarks</label>
              <p className="mt-1 text-secondary-900 whitespace-pre-wrap">{permit.remarks}</p>
            </div>
          )}
        </div>
      )}

      {/* Transfer Information */}
      {(permit.transfer_to || permit.transfer_permit_no || permit.transfer_date) && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">Transfer Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {permit.transfer_to && (
              <div>
                <label className="text-sm font-medium text-secondary-700">Transferred To</label>
                <p className="mt-1 text-secondary-900">{permit.transfer_to}</p>
              </div>
            )}
            {permit.transfer_permit_no && (
              <div>
                <label className="text-sm font-medium text-secondary-700">Transfer Permit Number</label>
                <p className="mt-1 text-secondary-900">{permit.transfer_permit_no}</p>
              </div>
            )}
            {permit.transfer_date && (
              <div>
                <label className="text-sm font-medium text-secondary-700">Transfer Date</label>
                <p className="mt-1 text-secondary-900 flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-secondary-500" />
                  {formatDate(permit.transfer_date)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Workflow Information */}
      <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">Workflow Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="text-sm font-medium text-secondary-700">Submitted By</label>
              <p className="mt-1 text-secondary-900 flex items-center">
                <User className="h-4 w-4 mr-2 text-secondary-500" />
                {permit.submitter?.fullName || 'Unknown'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-secondary-700">Submitted At</label>
              <p className="mt-1 text-secondary-900 flex items-center">
                <Clock className="h-4 w-4 mr-2 text-secondary-500" />
                {permit.submittedAt ? formatDate(permit.submittedAt) : 'Not submitted'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-secondary-700">Created At</label>
              <p className="mt-1 text-secondary-600 text-sm">{formatTimeAgo(permit.createdAt)}</p>
            </div>
            {permit.reviewedBy && (
              <div>
                <label className="text-sm font-medium text-secondary-700">Reviewed By</label>
                <p className="mt-1 text-secondary-900">{permit.reviewer?.fullName}</p>
              </div>
            )}
            {permit.reviewedAt && (
              <div>
                <label className="text-sm font-medium text-secondary-700">Reviewed At</label>
                <p className="mt-1 text-secondary-900">{formatDate(permit.reviewedAt)}</p>
              </div>
            )}
            {permit.approvedBy && (
              <div>
                <label className="text-sm font-medium text-secondary-700">Approved By</label>
                <p className="mt-1 text-secondary-900">{permit.approver?.fullName}</p>
              </div>
            )}
            {permit.approvedAt && (
              <div>
                <label className="text-sm font-medium text-secondary-700">Approved At</label>
                <p className="mt-1 text-secondary-900">{formatDate(permit.approvedAt)}</p>
              </div>
            )}
          </div>
        </div>

      {/* Registry Documents */}
      {((permit.landRegistryDocument && permit.landRegistryDocument.isActive !== false) ||
        (permit.registryMapDocument && permit.registryMapDocument.isActive !== false)) && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">Registry Documents</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Land Registry Document */}
            {permit.landRegistryDocument && permit.landRegistryDocument.isActive !== false && (
              <div>
                <h4 className="text-sm font-medium text-secondary-700 mb-2 flex items-center">
                  Land Registry
                  <span className="ml-2 px-2 py-1 text-xs font-medium bg-secondary-100 text-secondary-800 rounded">
                    Optional
                  </span>
                </h4>
                <div
                  onClick={() => handleDownload('landRegistry')}
                  className={`bg-primary-50 border-2 border-primary-200 rounded-lg p-4 cursor-pointer transition-all hover:border-primary-400 hover:shadow-md ${
                    downloadingDoc === 'landRegistry' ? 'opacity-50 cursor-wait' : ''
                  }`}
                >
                  <div className="flex flex-col items-center text-center space-y-3">
                    {/* Document Preview */}
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      {getFilePreview(permit.landRegistryDocument, 'small', previewUrls.landRegistry)}
                    </div>

                    {/* Document Info */}
                    <div className="w-full">
                      <p className="text-sm font-semibold text-secondary-900 mb-1 truncate" title={permit.landRegistryDocument.originalName}>
                        {permit.landRegistryDocument.originalName}
                      </p>
                      <p className="text-xs text-secondary-600">
                        {(permit.landRegistryDocument.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>

                    {/* Download Indicator */}
                    <div className="flex items-center text-primary-600 text-xs font-medium">
                      <Download className="h-3 w-3 mr-1" />
                      {downloadingDoc === 'landRegistry' ? 'Downloading...' : 'Click to download'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Registry Map Document */}
            {permit.registryMapDocument && permit.registryMapDocument.isActive !== false && (
              <div>
                <h4 className="text-sm font-medium text-secondary-700 mb-2 flex items-center">
                  Registry Map
                  <span className="ml-2 px-2 py-1 text-xs font-medium bg-secondary-100 text-secondary-800 rounded">
                    Optional
                  </span>
                </h4>
                <div
                  onClick={() => handleDownload('registryMap')}
                  className={`bg-primary-50 border-2 border-primary-200 rounded-lg p-4 cursor-pointer transition-all hover:border-primary-400 hover:shadow-md ${
                    downloadingDoc === 'registryMap' ? 'opacity-50 cursor-wait' : ''
                  }`}
                >
                  <div className="flex flex-col items-center text-center space-y-3">
                    {/* Document Preview */}
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      {getFilePreview(permit.registryMapDocument, 'small', previewUrls.registryMap)}
                    </div>

                    {/* Document Info */}
                    <div className="w-full">
                      <p className="text-sm font-semibold text-secondary-900 mb-1 truncate" title={permit.registryMapDocument.originalName}>
                        {permit.registryMapDocument.originalName}
                      </p>
                      <p className="text-xs text-secondary-600">
                        {(permit.registryMapDocument.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>

                    {/* Download Indicator */}
                    <div className="flex items-center text-primary-600 text-xs font-medium">
                      <Download className="h-3 w-3 mr-1" />
                      {downloadingDoc === 'registryMap' ? 'Downloading...' : 'Click to download'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Supporting Documents */}
      {permit.scannedDocuments && permit.scannedDocuments.filter(doc => doc.isActive !== false).length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4 flex items-center">
            Supporting Documents
            <span className="ml-2 px-2 py-1 text-xs font-medium bg-secondary-100 text-secondary-800 rounded">
              Optional
            </span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {permit.scannedDocuments.filter(doc => doc.isActive !== false).map((document, index) => (
              <div
                key={index}
                onClick={() => handleDownload('supporting', index)}
                className={`bg-secondary-50 border-2 border-secondary-200 rounded-lg p-4 cursor-pointer transition-all hover:border-secondary-400 hover:shadow-md ${
                  downloadingDoc === `supporting-${index}` ? 'opacity-50 cursor-wait' : ''
                }`}
              >
                <div className="flex flex-col items-center text-center space-y-3">
                  {/* Document Preview */}
                  <div className="p-3 bg-white rounded-lg shadow-sm">
                    {getFilePreview(document, 'small', previewUrls.supporting[index])}
                  </div>

                  {/* Document Info */}
                  <div className="w-full">
                    <p className="text-sm font-semibold text-secondary-900 mb-1 truncate" title={document.originalName}>
                      {document.originalName}
                    </p>
                    <p className="text-xs text-secondary-600">
                      {(document.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>

                  {/* Download Indicator */}
                  <div className="flex items-center text-secondary-600 text-xs font-medium">
                    <Download className="h-3 w-3 mr-1" />
                    {downloadingDoc === `supporting-${index}` ? 'Downloading...' : 'Click to download'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit Requests History - Expandable Accordion */}
      {permit.status === 'Active' && (
        <div className="mb-6">
          <EditRequestsList permitId={permit.id} />
        </div>
      )}

      {/* Cancellation Requests History - Expandable Accordion */}
      <div className="mb-6">
        <CancellationRequestsList permitId={permit.id} />
      </div>

      {/* Enhanced Audit Trail History */}
      {['bo', 'eac', 'sdo', 'adc', 'dc'].includes(user.role) ? (
        <>
          <PermitHistoryDetail
            permitId={id}
            onDownloadZip={['adc', 'dc'].includes(user.role) ? handleDownloadAllZip : null}
            downloadingZip={downloadingZip}
            userRole={user.role}
          />

          {/* File Upload History - Only for ADC and DC */}
          {['adc', 'dc'].includes(user.role) && (
            <FileUploadHistory permitId={id} />
          )}
        </>
      ) : null}
    </div>
  );
};

export default PermitView;